import { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmailListInput } from '@/components/ui/EmailListInput';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ReportCriteria } from '@/lib/reportCriteria';
import {
  DATES_ALL_UNITS_REASON,
  DATES_UNRESOLVED_REASON,
  defaultSubscriptionCompany,
  projectCriteriaToCompany,
  subscribeBlockReason,
} from '@/lib/reportSubscribeRules';
import {
  createReportSubscription,
  type ReportCadence,
  type ReportRecipientInput,
} from '@/lib/reportSubscriptionsApi';

interface CompanyOption { id: string; name: string }
interface GroupOption {
  id: string;
  company_id: string | null;
  parent_group_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

interface ReportSubscribeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current report's serialized criteria — the subscription reproduces this. */
  criteria: ReportCriteria;
  /** Companies the caller may subscribe for (their current selection ∩ accessible). */
  companyOptions: CompanyOption[];
  /** Groups the selection may contain, to narrow it to the chosen company. */
  groups: GroupOption[];
  /** Why the report on screen can't be scheduled (subscribeBlockReason with the page's context). */
  blockReason?: string | null;
  currentUserId?: string;
  onCreated?: () => void;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// A small, honest set of the zones this fleet actually operates in — not a 400-entry dump.
const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/New_York', label: 'Eastern' },
];

/** Hour options as friendly 12-hour labels. */
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`,
}));


export function ReportSubscribeDrawer({
  open, onOpenChange, criteria, companyOptions, groups, blockReason: blockReasonProp, currentUserId, onCreated,
}: ReportSubscribeDrawerProps) {
  const groupCompanyById = useMemo(
    () => new Map(groups.filter((g) => g.company_id).map((g) => [g.id, g.company_id as string])),
    [groups],
  );
  const [name, setName] = useState('');
  // Start on the company the selected productions belong to, so the user lands
  // on the report they were looking at rather than an emptied selection.
  const [companyId, setCompanyId] = useState(
    () => defaultSubscriptionCompany(criteria.groupIds, groupCompanyById, companyOptions),
  );
  const [cadence, setCadence] = useState<ReportCadence>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('1'); // Monday
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [sendHour, setSendHour] = useState('8');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [sendToMe, setSendToMe] = useState(true);
  const [emails, setEmails] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // companyOptions can arrive empty then populate (direct /reports load — FilterContext
  // hydrates async), and the selection can change while the drawer is mounted. Keep
  // companyId valid: default to the first option whenever the current id isn't in the list.
  useEffect(() => {
    if (companyOptions.length > 0 && !companyOptions.some((c) => c.id === companyId)) {
      setCompanyId(defaultSubscriptionCompany(criteria.groupIds, groupCompanyById, companyOptions));
    }
  }, [companyOptions, companyId, criteria.groupIds, groupCompanyById]);

  // Each time the drawer opens, start on the selected productions' company.
  useEffect(() => {
    if (open) setCompanyId(defaultSubscriptionCompany(criteria.groupIds, groupCompanyById, companyOptions));
    // Only on open: changing company while open is the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A subscription covers ONE company: narrow the selection to it. A selection
  // with nothing there, or no selection at all, is never widened to the whole
  // company; reporting on all of its units is an explicit switch (spec §2). The
  // switch is a choice about THIS selection and company, so it turns off when
  // either changes or the drawer closes.
  const [allUnits, setAllUnits] = useState(false);
  const selectionKey = `${criteria.groupScope}:${criteria.groupIds.join(',')}`;
  useEffect(() => { setAllUnits(false); }, [companyId, selectionKey, open]);
  const projection = useMemo(
    () => projectCriteriaToCompany(criteria, companyId, groupCompanyById, { allUnits, groups }),
    [criteria, companyId, groupCompanyById, allUnits, groups],
  );
  const companyName = companyOptions.find((c) => c.id === companyId)?.name ?? 'this company';
  const pageBlockReason = blockReasonProp === undefined ? subscribeBlockReason(criteria) : blockReasonProp;
  // "Use production dates" re-resolved for this company's productions only.
  const datesReason = projection.datesUnresolved
    ? (allUnits || projection.criteria.groupScope === 'all' ? DATES_ALL_UNITS_REASON : DATES_UNRESOLVED_REASON)
    : null;
  const blockReason = pageBlockReason ?? datesReason;
  const droppedCount = projection.droppedGroupIds.length;
  const otherCompanies = criteria.companyIds.filter((id) => id !== companyId).length;
  const needsAllUnits = projection.needsAllUnits;

  const recipientCount = (sendToMe && currentUserId ? 1 : 0) + emails.length;
  const canSubmit = name.trim().length > 0 && !!companyId && recipientCount > 0 && !submitting
    && !blockReason && !needsAllUnits;

  const reset = () => {
    setName(''); setCadence('weekly'); setDayOfWeek('1'); setDayOfMonth('1');
    setSendHour('8'); setTimezone('America/Los_Angeles'); setSendToMe(true);
    setEmails([]); setAllUnits(false);
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const recipients: ReportRecipientInput[] = [
        ...(sendToMe && currentUserId ? [{ userId: currentUserId } as ReportRecipientInput] : []),
        ...emails.map((email) => ({ email } as ReportRecipientInput)),
      ];
      await createReportSubscription({
        companyId,
        name: name.trim(),
        criteria: projection.criteria,
        schedule: {
          cadence,
          sendHour: Number(sendHour),
          ...(cadence === 'weekly' ? { dayOfWeek: Number(dayOfWeek) } : {}),
          ...(cadence === 'monthly' ? { dayOfMonth: Number(dayOfMonth) } : {}),
        },
        timezone,
        recipients,
      });
      toast.success('Subscription created. Reports will be sent on schedule.');
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the subscription.');
    } finally {
      setSubmitting(false);
    }
  };

  const cadenceSummary = useMemo(() => {
    const at = HOURS.find((h) => h.value === sendHour)?.label ?? '';
    if (cadence === 'daily') return `Every day at ${at}`;
    if (cadence === 'weekly') return `Every ${DAYS_OF_WEEK[Number(dayOfWeek)]} at ${at}`;
    return `On day ${dayOfMonth} of each month at ${at}`;
  }, [cadence, dayOfWeek, dayOfMonth, sendHour]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Subscribe to this report</SheetTitle>
          <SheetDescription>
            Emails this report on a schedule using the filters currently applied. Recipients can unsubscribe anytime.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-5">
          {blockReason && (
            <p role="status" className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
              {blockReason}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="sub-name">Name</Label>
            <Input id="sub-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Weekly solar summary" maxLength={120} />
          </div>

          {companyOptions.length > 1 && (
            <div className="space-y-2">
              <Label>Company</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger aria-label="Company"><SelectValue placeholder="Select a company" /></SelectTrigger>
                <SelectContent>
                  {companyOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {!pageBlockReason && (droppedCount > 0 || (otherCompanies > 0 && needsAllUnits === 'no_selection')) && needsAllUnits !== 'none_in_company' && (
            <p className="text-xs text-muted-foreground -mt-2">
              {droppedCount === 0
                ? `Only ${companyName} is included.`
                : droppedCount === 1
                  ? '1 production or site from another company is not included.'
                  : `${droppedCount} productions or sites from other companies are not included.`}
              {' '}A subscription covers one company.
            </p>
          )}

          {!pageBlockReason && (needsAllUnits || allUnits) && (
            <div className="space-y-2 rounded-md border border-border p-3">
              {!allUnits && needsAllUnits === 'none_in_company' && (
                <p className="text-sm text-foreground">
                  None of the selected productions belong to {companyName}. Choose productions in {companyName}, or report on all of its units.
                </p>
              )}
              {!allUnits && needsAllUnits === 'no_selection' && (
                <p className="text-sm text-foreground">
                  No production is selected. Choose a production, or report on all units in {companyName}.
                </p>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-foreground">Report on all units in {companyName}</span>
                <Switch checked={allUnits} onCheckedChange={setAllUnits} aria-label={`Report on all units in ${companyName}`} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select value={cadence} onValueChange={(v) => setCadence(v as ReportCadence)}>
                <SelectTrigger aria-label="Frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cadence === 'weekly' && (
              <div className="space-y-2">
                <Label>Day</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger aria-label="Day of week"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {cadence === 'monthly' && (
              <div className="space-y-2">
                <Label>Day of month</Label>
                <Select value={dayOfMonth} onValueChange={setDayOfMonth}>
                  <SelectTrigger aria-label="Day of month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Time</Label>
              <Select value={sendHour} onValueChange={setSendHour}>
                <SelectTrigger aria-label="Time"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {HOURS.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Time zone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger aria-label="Time zone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">{cadenceSummary}</p>

          <div className="space-y-3 border-t border-border pt-4">
            <Label>Recipients</Label>
            {currentUserId && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Send to me</span>
                <Switch checked={sendToMe} onCheckedChange={setSendToMe} aria-label="Send to me" />
              </div>
            )}
            <EmailListInput value={emails} onChange={setEmails} />
            {recipientCount === 0 && <p className="text-xs text-muted-foreground">Add at least one recipient.</p>}
          </div>
        </div>

        <SheetFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create subscription
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
